import {
    Box,
    Button,
    Theme,
    Typography,
    useTheme
} from "@mui/material";
import CircleIcon from '@mui/icons-material/Circle';
import CustomNavChart from "../../../dashboard/chart/CustomNavChart";
import {
    PortfolioHealthButton,
    PortfolioHealthProps
} from "./types";
import {
    useEffect,
    useState
} from "react";
import {
    useDispatch,
    useSelector
} from "react-redux";
import { RootStateType } from "../../../../redux-store/reducers";
import { GetBenchmarkPerformanceResponseBody } from "../../../../redux-store/types/analytics";
import { getBenchmarkPerformance } from "../../../../redux-store/actions/analytics";
import { cloneDeep } from "lodash";
import { PortfolioHealthLoading } from "../../loadingComponents";
import { NO_DATA_AVAILABLE } from "../../../../utils/constant";

const navChartButtons = [{
    type: 'month',
    count: 6,
    text: '6M'
}, {
    type: 'year',
    count: 1,
    text: '1Y'
},
{
    type: 'all',
    text: 'Inception'
}]

const currentDate = new Date();
const cutoffDate = (n: number) => new Date(currentDate.getFullYear() - n, 0, 1);

export default function PortfolioHealth({
    clientCode,
    portfolioId
}: PortfolioHealthProps): JSX.Element {
    const theme = useTheme()
    const dispatch = useDispatch();

    const [isLoading, setLoading] = useState<boolean>(false)
    const [chartData, setChartData] = useState<{
        originalData: {
            data1: Array<[number, number]>;
            data2: Array<[number, number]>;
            label1: string;
            label2: string;
        };
        filteredData: {
            data1: Array<[number, number]>;
            data2: Array<[number, number]>;
            label1: string;
            label2: string;
        },
        message: string;
        isSuccess: boolean;
    }>({
        originalData: {
            data1: [],
            data2: [],
            label1: '',
            label2: '',
        },
        filteredData: {
            data1: [],
            data2: [],
            label1: '',
            label2: '',
        },
        message: "",
        isSuccess: true
    });
    const [activePortfolioHealthBtn, setActivePortfolioHealthBtn] = useState<string>('Inception')

    const { iwappRmId = "" } = useSelector((state: RootStateType) => state.id);

    const fetchData = async (clientCode: string, portfolioId: string) => {
        try {
            setLoading(true)

            const payload = {
                rmId: `${iwappRmId}`,
                clientCode,
                portfolioId,
                assetGroup: null
            }

            const response = await (dispatch(getBenchmarkPerformance(payload)) as unknown as Promise<GetBenchmarkPerformanceResponseBody>);

            const originalResponse = cloneDeep(response ?? {})
            const filteredResponse = cloneDeep(response ?? {})

            setChartData({
                originalData: {
                    data1: originalResponse?.result?.data1 ?? [],
                    data2: originalResponse?.result?.data2 ?? [],
                    label1: originalResponse?.result?.label1 ?? "",
                    label2: originalResponse?.result?.label2 ?? "",
                },
                filteredData: {
                    data1: filteredResponse?.result?.data1 ?? [],
                    data2: filteredResponse?.result?.data2 ?? [],
                    label1: filteredResponse?.result?.label1 ?? "",
                    label2: filteredResponse?.result?.label2 ?? "",
                },
                message: originalResponse?.message ?? "",
                isSuccess: originalResponse?.isSuccess ?? true
            })

        } catch (error) {
            console.log((error as Error)?.message);
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData(clientCode, portfolioId)
    }, [clientCode, portfolioId])

    const handlePortfolioHealthButtonClick = (currentButton: string) => {
        setActivePortfolioHealthBtn(currentButton);

        if (currentButton === '6M') {
            const sixMonthsAgo = new Date();
            sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

            setChartData(prevState => ({
                ...prevState,
                filteredData: {
                    ...prevState.originalData,
                    data1: prevState.originalData?.data1.filter(([timestamp, _]) => timestamp >= sixMonthsAgo.getTime()),
                    data2: prevState.originalData?.data2.filter(([timestamp, _]) => timestamp >= sixMonthsAgo.getTime()),
                }
            }))

        } else if (currentButton === '1Y') {
            setChartData(prevState => ({
                ...prevState,
                filteredData: {
                    ...prevState.originalData,
                    data1: prevState.originalData?.data1.filter(([timestamp, _]) => timestamp >= cutoffDate(1).getTime()),
                    data2: prevState.originalData?.data2.filter(([timestamp, _]) => timestamp >= cutoffDate(1).getTime()),
                }
            }))
        } else if (currentButton === 'Inception') {
            setChartData(prevState => ({
                ...prevState,
                filteredData: prevState.originalData
            }))
        }
    };

    if (isLoading) {
        return <PortfolioHealthLoading />
    }

    if (!isLoading && !chartData?.isSuccess) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        paddingBottom: "20px"
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            lineHeight: '25px',
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary,
                        })}
                    >
                        Portfolio Health
                    </Typography>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Typography
                        variant="body_600"
                        sx={(theme: Theme) => ({
                            fontWeight: 500,
                            lineHeight: "22.5px",
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary,
                            textAlign: "center",
                            width: "100%"
                        })}
                    >
                        {NO_DATA_AVAILABLE}
                    </Typography>
                </Box>
            </Box>
        )
    }

    return (
        <Box>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                rowGap: '10px',
                marginBottom: "35px"
            }}>
                <Typography
                    variant="h2"
                    sx={(theme: Theme) => ({
                        lineHeight: '25px',
                        letterSpacing: "0.01px",
                        color: theme.palette.text.primary
                    })}
                >
                    Portfolio Health
                </Typography>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: { sm: 'flex-end', md: 'flex-start' },
                    columnGap: { sm: 0, md: 2, lg: 3 },
                }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            columnGap: '7px'
                        }}>
                        <CircleIcon
                            style={{
                                color: theme.palette.primary[500],
                                width: '11px',
                                height: '11px',
                            }}
                        />
                        <Typography
                            component='span'
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary,
                                lineHeight: '20px'
                            })}
                        >
                            {chartData?.filteredData?.label1}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            columnGap: '7px'
                        }}>
                        <CircleIcon
                            style={{
                                color: theme.palette.warning[500],
                                width: '11px',
                                height: '11px',
                            }}
                        />
                        <Typography
                            component='span'
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary,
                                lineHeight: '20px'
                            })}
                        >
                            {chartData?.filteredData?.label2}
                        </Typography>
                    </Box>
                </Box>
            </Box>
            <Box>
                <CustomNavChart
                    data={chartData?.filteredData?.data1}
                    data1={chartData?.filteredData?.data2}
                    label1={chartData?.filteredData?.label1}
                    label2={chartData?.filteredData?.label2}
                    dynamicY={true}
                />
            </Box>
            <Box
                sx={{
                    mt: '45px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    columnGap: '10px'
                }}
            >
                {navChartButtons?.map((individualBtn: PortfolioHealthButton, index: number) => {
                    return (
                        <Button
                            key={index}
                            sx={(theme: Theme) => ({
                                ...theme.typography.body_500,
                                letterSpacing: '0.02px',
                                borderRadius: '7px',
                                padding: '5px 15px',
                                border: 'none',
                                cursor: 'pointer',
                                color: { color: activePortfolioHealthBtn === individualBtn?.text ? theme.palette.common.white : theme.palette.text.secondary },
                                '&:hover': {
                                    border: 'none',
                                }
                            }
                            )}
                            variant={activePortfolioHealthBtn === individualBtn?.text ? 'contained' : 'outlined'}
                            onClick={() => handlePortfolioHealthButtonClick(individualBtn?.text)}
                        >
                            {individualBtn?.text}
                        </Button>
                    )
                })}
            </Box>

        </Box>
    )
}